/* THE single source of truth for the supported agent kinds (contract §9a).
 *
 * Everything that enumerates kinds derives from this module:
 *   - the `AgentKind` union type (re-exported by analytics/types for the
 *     contract types) is derived from AGENT_KIND_VALUES,
 *   - presentation (label, glyph, badge tone, colored tile) joins onto it in
 *     ./kinds.tsx and ./logos via exhaustive Record<AgentKind, …> maps, so
 *     adding a value here fails the build until every surface covers it,
 *   - the onboarding tile grid, the agent-detail kind picker, and the docs
 *     integration mapping all read the joined specs.
 *
 * To add a kind: append its value here, then follow the compiler.
 *
 * ⚠️ The gateway validates `kind` on agent create/update — a new value also
 * needs backend support before it can be persisted. */

/** Order is the design's tile-grid order (Figma 199:8794) and drives every
 * kind list in the app. */
export const AGENT_KIND_VALUES = [
  "claude_code",
  "langchain",
  "codex",
  // Not fully supported yet — uncomment to re-enable, then follow the
  // compiler to restore its presentation entries (kinds.tsx, logos):
  // "cursor",
  "other",
] as const;

export type AgentKind = (typeof AGENT_KIND_VALUES)[number];

/** The product-default kind. Creation flows deliberately pre-select nothing — the
 *  onboarding picker opens empty so every agent carries a type its owner chose — so
 *  this is the fallback for code that must name a kind without asking. */
export const DEFAULT_AGENT_KIND: AgentKind = "claude_code";

/** Which docs-snippet integration a kind maps to ("generic" = catch-all). */
export type AgentIntegration =
  "claude-code" | "langchain" | "codex" | "generic";
